/**
 * Campaigns Page
 * List and manage marketing campaigns
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getCampaigns,
  searchCampaigns,
  type CampaignListRow,
} from "../services/campaigns.service";
import { formatDate } from "../utils/formatters";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import StagedSendModal from "../components/campaigns/StagedSendModal";
import DeleteCampaignButton from "../components/campaigns/DeleteCampaignButton";
import { PlusIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import type { CampaignStatus, CampaignType } from "../types/campaign.types";

const Campaigns: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showStagedSend, setShowStagedSend] = useState(false);

  // Debounce search input - wait 500ms after user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Fetch campaigns or search results
  const {
    data: campaigns,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["campaigns", searchQuery],
    queryFn: () => {
      if (searchQuery.trim()) {
        return searchCampaigns(searchQuery.trim());
      }
      return getCampaigns();
    },
    // Keep showing the current list while a new search term loads. Without
    // this, each new term put the query back into its first-load state, the
    // page swapped to the full-page spinner, and the search box lost focus.
    placeholderData: keepPreviousData,
  });

  const getStatusBadgeVariant = (
    status: CampaignStatus,
  ): "default" | "success" | "error" | "warning" => {
    switch (status) {
      case "active":
        return "success";
      case "completed":
        return "default";
      case "cancelled":
        return "error";
      case "draft":
      default:
        return "warning";
    }
  };

  const getStatusLabel = (status: CampaignStatus): string => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // 2026-05-27: separate visual indicator from status so operators can
  // see at a glance whether a row is a multi-step Journey or a one-off
  // Single Send. Independent of Draft/Active status. Legacy 'marketing'
  // rows show a dim "Untyped" chip prompting re-categorization.
  const getTypeBadge = (
    type: CampaignType | undefined,
  ): { label: string; variant: "secondary" | "info" | "success" | "gray" } => {
    switch (type) {
      case "journey":
        return { label: "Journey", variant: "secondary" };
      case "single_send":
        return { label: "Single Send", variant: "info" };
      case "event_ticket":
        return { label: "Event Tickets", variant: "success" };
      case "event_reminder":
        return { label: "Event Reminder", variant: "success" };
      case "marketing":
      default:
        return { label: "Untyped", variant: "gray" };
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading campaigns..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Campaigns</h1>
          <p className="mt-2 text-gray-600">
            Manage marketing campaigns and distribution flights
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowStagedSend(true)}
            title="Split a large static list into wave-sized lists to send over days"
          >
            Plan staged send
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate("/campaigns/create")}
            className="flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Create Campaign
          </Button>
        </div>
      </div>
      <StagedSendModal
        isOpen={showStagedSend}
        onClose={() => setShowStagedSend(false)}
      />

      {/* Search Bar */}
      <Card variant="outlined" padding="sm">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search campaigns by name or description..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {isFetching && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              Searching...
            </span>
          )}
        </div>
      </Card>

      {/* Shown here, not in place of the page, so the search box keeps its text and focus */}
      {error && (
        <Card
          variant="outlined"
          padding="md"
          className="border-red-200 bg-red-50"
        >
          <p className="text-red-800">
            Error loading campaigns. Please try again.
          </p>
        </Card>
      )}

      {/* Campaigns Table */}
      <Card variant="outlined" padding="none">
        {campaigns && campaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Flights
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vouchers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Redemption
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign: CampaignListRow) => (
                  <tr
                    key={campaign.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {campaign.name}
                        </div>
                        {campaign.description && (
                          <div className="text-sm text-gray-500 line-clamp-1">
                            {campaign.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const t = getTypeBadge(campaign.campaign_type);
                        return <Badge variant={t.variant}>{t.label}</Badge>;
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={getStatusBadgeVariant(campaign.status)}>
                        {getStatusLabel(campaign.status)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {campaign.start_date && campaign.end_date ? (
                        <div>
                          <div>{formatDate(campaign.start_date)}</div>
                          <div className="text-gray-500">
                            to {formatDate(campaign.end_date)}
                          </div>
                        </div>
                      ) : campaign.start_date ? (
                        <div>From {formatDate(campaign.start_date)}</div>
                      ) : (
                        <div className="text-gray-400">No dates set</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {campaign.total_flights}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <div>
                          {campaign.total_vouchers_generated.toLocaleString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {campaign.total_vouchers_generated > 0 ? (
                        <div>
                          <div>
                            {campaign.total_vouchers_redeemed.toLocaleString()}{" "}
                            redeemed
                          </div>
                          <div className="text-gray-500">
                            {(
                              (campaign.total_vouchers_redeemed /
                                campaign.total_vouchers_generated) *
                              100
                            ).toFixed(1)}
                            %
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-400">-</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/campaigns/${campaign.id}`);
                          }}
                        >
                          View Details
                        </Button>
                        <DeleteCampaignButton
                          campaign={campaign}
                          flightStatuses={campaign.flight_statuses}
                          onDeleted={() =>
                            queryClient.invalidateQueries({
                              queryKey: ["campaigns"],
                            })
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !error && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                {searchQuery
                  ? "No campaigns found matching your search."
                  : "No campaigns yet."}
              </p>
              <p className="text-gray-400 mt-2">
                {searchQuery
                  ? "Try a different search term."
                  : "Create your first campaign to get started."}
              </p>
              {!searchQuery && (
                <Button
                  variant="primary"
                  onClick={() => navigate("/campaigns/create")}
                  className="mt-4"
                >
                  <PlusIcon className="h-5 w-5 mr-2 inline" />
                  Create Campaign
                </Button>
              )}
            </div>
          )
        )}
      </Card>
    </div>
  );
};

export default Campaigns;
